import {
  Component,
  EventEmitter,
  inject,
  InjectFlags,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthApiService, MeApiService } from '@digilize/shared/data/services/src';
import { AuthService } from '@digilize/shared/feature/modules/auth/src';
import { dateBeforeNow } from '@digilize/shared/utils/validators/src';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, forkJoin, Subscription } from 'rxjs';

import { ManagePasswordComponent } from '../../../components/manage-password/manage-password.component';
import { EducationService } from '../../../services/education.service';
import { ErrorService } from '../../../services/error.service';
import { MeProfileService } from '../../../services/me-profile.service';
import { WizardService } from '../../../services/wizard.service';

export const DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export class MyDateAdapter extends NativeDateAdapter {
  parse(value: string) {
    let it = value.split('/');
    if (it.length == 3) return new Date(+it[2], +it[1] - 1, +it[0], 12);
  }
}

@Component({
  selector: 'bwc-general-information',
  templateUrl: './general-information.component.html',
  styleUrls: ['./general-information.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MyDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: LOCALE_ID, useValue: 'pl-PL' },
  ],
})
export class GeneralInformationComponent implements OnInit, OnDestroy {
  @Input() formGroup: FormGroup;
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @Input() editing = false;
  @Input() accountActivated = false;
  @Output() formEmit = new EventEmitter();
  @Output() emitClose = new EventEmitter();

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  tempForm: FormGroup;
  countriesOptions = [];
  dataLoaded = false;
  genderCreated = false;
  emailExists = false;
  originalEmail = '';
  showLogInCont = false;
  genderList = ['female', 'male', 'other'];
  genderOptions = [];
  selectedLang: string;
  subSink: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private meApiService: MeApiService,
    private authApiService: AuthApiService,
    private authService: AuthService,
    private meProfileService: MeProfileService,
    public errorService: ErrorService,
    private educationService: EducationService,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {}

  get emailChanged(): boolean {
    return this.originalEmail !== '' && this.form?.get('email')?.value !== this.originalEmail;
  }

  ngOnInit(): void {
    this.selectedLang = this.translateService.getDefaultLang();

    this.createGenderOptions();
    this.subSink.push(
      this.translateService.onLangChange.subscribe((langChangeEvent) => {
        this.getCountries(langChangeEvent.lang);
        this.createGenderOptions();
      })
    );

    if (this.formGroup !== undefined) {
      this.editing = true;
      this.form = this.createForm();
      this.patchUserData(this.formGroup.value);
      this.getCountries(this.selectedLang, true);
    } else {
      this.form = this.createForm();
      this.getData(this.selectedLang);
    }

    // this.listenToEmailChanges();
    this.form.get('email').valueChanges.subscribe((data) => {
      this.emailExists = false;
    });
  }

  createGenderOptions() {
    this.genderOptions = [];
    this.genderOptions = [
      ...this.genderList.map((item) => ({ text: this.translateService.instant('global.' + item), val: item })),
    ];
    this.genderCreated = true;
  }

  patchUserData(formVals) {
    const vals = { ...formVals };
    vals.birthdayDate = this.formatDate(formVals.birthday);
    this.form.patchValue(vals);
    this.originalEmail = formVals.email || '';
  }

  listenToEmailChanges() {
    this.form
      .get('email')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((value) => {
        this.setEmailExistanceStatus(this.form.value.email);
      });
  }

  redirectToLogIn() {
    this.authService.logout();
  }

  createForm() {
    return this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.minLength(7), Validators.pattern(/[0-9+\- ]/)]],
      city: [''],
      country: [''],
      birthday: ['', dateBeforeNow()],
      birthdayDate: [''],
      gender: [''],
    });
  }

  getCountries(lang: string, loaded?) {
    this.educationService.getCountries(lang).subscribe((data) => {
      const languages = data.data.map((item) => ({
        text: item.common_name,
        val: item.alpha2_code,
      }));
      this.countriesOptions = [...languages];
      if (loaded) {
        this.dataLoaded = true;
      }
    });
  }

  setEmailExistanceStatus(email) {
    if (email) {
      this.authApiService.doesEmailExists(email).subscribe((data) => {
        this.emailExists = data.exists;
      });
    }
  }

  getData(lang: string) {
    forkJoin({
      countriesData: this.educationService.getCountries(lang),
      userData: this.meApiService.getMe(),
    }).subscribe((data: any) => {
      this.countriesOptions = data.countriesData.data.map((item) => ({
        text: item.name,
        val: item.code,
      }));
      this.form.patchValue({
        first_name: data.userData.first_name,
        last_name: data.userData.last_name,
        email: data.userData.email,
        phone: data.userData.phone,
        city: data.userData.city,
        country: data.userData.country,
        birthday: data.userData.birthday,
        birthdayDate: this.formatDate(data.userData.birthday),
        gender: data.userData.gender,
      });

      this.originalEmail = data.userData.email || '';
      this.dataLoaded = true;
      // this.setEmailExistanceStatus(data.userData.email);
    });
  }

  public getFormControl(formControlName: string) {
    return this.form.get(formControlName) as FormControl;
  }

  handleFilteredOptionClick(event, name) {
    this.getFormControl(name).setValue(event.val);
  }

  onSubmit() {
    if (this.form.valid) {
      this.sendData(this.form.value);
    }
  }

  onEdit() {
    if (this.form.valid) {
      this.sendData(this.form.value, true);
    }
  }

  sendData(data, close?) {
    const genInfo = {
      ...data,
      nickname: '',
      description: '',
      post_code: '',
      street: '',
      house_number: '',
      street_number: '',
    };

    this.meProfileService.postProfileWithAdress(genInfo).subscribe(
      (resp) => {
        if (resp.email_change_pending) {
          const dialogRef = this.dialog.open(ManagePasswordComponent, {
            disableClose: false,
            width: '670px',
            data: {
              type: 'email_change',
              email: this.form.value.email,
              password_created: true,
              checkYourEmail: true,
              resendEmail: false,
              account_activated: false,
              activation_error: false,
            },
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result?.activated) {
              this.proceedAfterSubmit(close);
            }
          });
        } else {
          this.proceedAfterSubmit(close);
        }
      },
      (error) => {
        if (error.error.error.type === 'already_exists') {
          this.emailExists = true;
        }
      }
    );
  }

  private proceedAfterSubmit(close?: boolean) {
    if (close) {
      this.emitClose.emit('gen_info');
      this.formEmit.emit(this.form);
    } else {
      this.wizardService?.goToNext();
    }
  }

  onBirthdateChange(event) {
    const format = this.convertToIsoDateFormat(event.target.value);
    this.form.get('birthday').setValue(format);
    this.form.get('birthday').updateValueAndValidity();
  }

  handleDateSelection(event) {
    const selectedDate = event.value;
    const adjustedDate = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000);
    const selectedDateISO = adjustedDate.toISOString();
    const datePartOnly = selectedDateISO.substring(0, selectedDateISO.indexOf('T'));
    this.form.get('birthdayDate').setValue(this.formatDate(datePartOnly));
    this.form.get('birthday').setValue(selectedDate, { emitModelToViewChange: false });
    this.form.get('birthday').updateValueAndValidity();
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    } else {
      const date = new Date(dateString);
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }

  convertToIsoDateFormat(dateString: string): string {
    const isFullyFilled = /^\d{2}-\d{2}-\d{4}$/.test(dateString);

    if (isFullyFilled) {
      const [day, month, year] = dateString.split('-');
      const date = new Date(`${year}-${month}-${day}`);
      return date.toISOString();
    } else {
      return dateString;
    }
  }

  checkIfDateEmpty(value) {
    if (!value || value.length === 0) {
      return 'dd-mm-yyyy';
    } else {
      return '';
    }
  }

  close() {
    this.emitClose.emit('gen_info');
  }

  goPrev() {
    this.wizardService?.goToPrevious();
  }

  ngOnDestroy(): void {
    this.subSink.forEach((sub) => sub.unsubscribe());
  }
}

export interface GeneralInfo {
  first_name: string;
  last_name: string;
  email: string;
  city: string;
  country: string;
  birthday: Date;
  gender: string;
}
